import { Phone, Envelope, Clock, MapPin, IdentificationCard, FileText, Users, CheckCircle } from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';

export const BookingPage = () => {
  const checklist = [
    {
      icon: IdentificationCard,
      title: "Valid Photo ID",
      description: "Driver's license, passport, or state-issued ID for every signer.",
    },
    {
      icon: FileText,
      title: "All Document Pages",
      description: "Bring the complete document — please don't sign anything in advance.",
    },
    {
      icon: Users,
      title: "All Signers Present",
      description: "Every person required to sign must be physically (or virtually for RON) present.",
    },
  ];

  return (
    <main data-testid="booking-page">
      {/* Header */}
      <section className="bg-[#800000] py-32 pt-40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">Schedule Online · 24/7</span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold text-white mb-5">
            Book Your <span className="italic gold-shimmer">Appointment</span>
          </h1>
          <p className="text-lg text-white/85 max-w-2xl leading-relaxed">
            Four quick steps. India will come to you in Nashville, RON with you anywhere in the world, or coordinate a vetted notary from her nationwide signing-agent network.
          </p>
        </div>
      </section>

      {/* Contact bar */}
      <section className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-[#1A1A1A]">
            <span className="text-sm font-semibold tracking-wider uppercase text-[#800000]">Prefer to call?</span>
            <a href={`tel:${BRAND.phoneRaw}`} data-testid="booking-phone-link" className="flex items-center gap-2 hover:text-[#800000] transition-colors font-semibold">
              <Phone weight="fill" size={18} className="text-[#800000]" />
              {BRAND.phone}
            </a>
            <span className="hidden sm:inline text-[#D4AF37]">·</span>
            <a href={`mailto:${BRAND.email}`} data-testid="booking-email-link" className="flex items-center gap-2 hover:text-[#800000] transition-colors font-medium text-sm break-all">
              <Envelope weight="fill" size={18} className="text-[#800000]" />
              {BRAND.email}
            </a>
          </div>
        </div>
      </section>

      {/* What to bring checklist */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Before Your Appointment</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] mb-3">
              What to Bring
            </h2>
            <p className="text-[#5C5C5C] text-sm sm:text-base max-w-xl mx-auto">
              A quick checklist to make sure your appointment goes smoothly.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {checklist.map((item, idx) => (
              <div
                key={idx}
                data-testid={`checklist-item-${idx}`}
                className="bg-[#FBF8F1] border border-[#E8E2D5] rounded-sm p-6 hover:border-[#D4AF37]/50 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                    <item.icon weight="duotone" size={22} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle weight="fill" size={14} className="text-[#800000]" />
                      <h3 className="font-serif-display text-lg font-semibold text-[#800000] leading-tight">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-[#5C5C5C] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GHL Booking Form */}
      <section className="py-16 bg-[#FBF8F1]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Step 1 of 1</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] mb-3">
              Request Your Appointment
            </h2>
            <p className="text-[#5C5C5C] text-sm sm:text-base max-w-xl mx-auto">
              Fill out the form below. India will personally confirm your appointment by phone or email — usually within the hour.
            </p>
          </div>
          <div
            data-testid="booking-ghl-form"
            className="bg-white shadow-2xl rounded-sm border-t-4 border-[#D4AF37] overflow-hidden"
          >
            <iframe
              src="https://api.leadconnectorhq.com/widget/form/36gZoJkMhocEE5hHdL78"
              style={{ width: '100%', height: '485px', border: 'none' }}
              id="inline-booking-36gZoJkMhocEE5hHdL78"
              data-layout='{"id":"INLINE"}'
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="Contact Form"
              data-height="485"
              data-layout-iframe-id="inline-booking-36gZoJkMhocEE5hHdL78"
              data-form-id="36gZoJkMhocEE5hHdL78"
              title="Book an Appointment"
            />
          </div>
        </div>
      </section>

      {/* Info strip */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#800000]/5 flex items-center justify-center mx-auto mb-4">
                <Clock weight="duotone" size={26} className="text-[#800000]" />
              </div>
              <h3 className="font-serif-display text-lg font-semibold text-[#800000] mb-2">Hours</h3>
              <p className="text-sm text-[#5C5C5C]">
                Available 24 / 7 · 365<br />
                Emergency same-day welcome
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#800000]/5 flex items-center justify-center mx-auto mb-4">
                <MapPin weight="duotone" size={26} className="text-[#800000]" />
              </div>
              <h3 className="font-serif-display text-lg font-semibold text-[#800000] mb-2">Service Area</h3>
              <p className="text-sm text-[#5C5C5C]">
                Nashville · Brentwood · Franklin<br />
                Hendersonville · All of Middle TN
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#800000]/5 flex items-center justify-center mx-auto mb-4">
                <Phone weight="duotone" size={26} className="text-[#800000]" />
              </div>
              <h3 className="font-serif-display text-lg font-semibold text-[#800000] mb-2">Same-Day</h3>
              <p className="text-sm text-[#5C5C5C]">
                Urgent? Call directly — <br />
                {BRAND.phone}
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
